/**
 * sizedAvatarUrl — request appropriately-sized avatars from common providers.
 *
 * Google and GitHub serve oversized images by default (96px+) which we
 * downscale to 16-28px on render — wasting ~3-5x the bytes. This rewrites
 * the URL to ask for a render-size variant.
 *
 * No-op for other providers (custom uploads, Supabase storage, etc.) so
 * adoption is incremental and safe.
 *
 * @param url   The original avatar URL (may be undefined/null/empty).
 * @param sizePx The intended display size in CSS pixels. We request 2x for retina.
 */
export function sizedAvatarUrl(
  url: string | null | undefined,
  sizePx: number
): string | undefined {
  if (!url) return undefined;
  const target = Math.max(16, Math.round(sizePx * 2));

  try {
    const u = new URL(url);

    if (
      u.hostname.endsWith(".googleusercontent.com") ||
      u.hostname === "googleusercontent.com"
    ) {
      return rewriteGoogle(url, target);
    }

    if (u.hostname === "avatars.githubusercontent.com") {
      u.searchParams.set("s", String(target));
      return u.toString();
    }
  } catch {
    return url;
  }

  return url;
}

function rewriteGoogle(url: string, target: number): string {
  const sizeToken = `=s${target}-c`;
  // Most Google avatar URLs end with =s{N}-c or =s{N}. Replace the trailing size.
  if (/=s\d+(-c)?$/.test(url)) {
    return url.replace(/=s\d+(-c)?$/, sizeToken);
  }
  // No size token present — append one.
  return `${url}${sizeToken}`;
}
